import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { BarChart3, Download, PieChart, LineChart } from 'lucide-react';
import { WidgetPanel, RoundedBarChart } from './DashboardShared';
import { salesOf, purchaseOrders, whPurchaseOrders } from '../../../data/store';

export const ReportsModule = ({ org }) => {
  const metrics = useMemo(() => {
    const sales = salesOf(org.id);
    const purch = [
      ...purchaseOrders.filter((p) => p.orgId === org.id),
      ...whPurchaseOrders.filter((p) => p.orgId === org.id)
    ];

    const totalRevenue = sales.reduce((s, x) => s + (Number(x.total) || 0), 0);
    const totalCost = purch.reduce((s, x) => s + (Number(x.amount) || 0), 0);
    const margin = totalRevenue - totalCost;
    
    // Category-wise revenue
    const catSales = {};
    sales.forEach(sale => {
      (sale.lines || []).forEach(line => {
        catSales[line.categoryId || 'Uncategorized'] = (catSales[line.categoryId || 'Uncategorized'] || 0) + ((Number(line.price) || 0) * (Number(line.qty) || 0));
      });
    });

    const topCategories = Object.entries(catSales)
      .sort((a,b) => b[1] - a[1])
      .slice(0, 4);

    // Mock chart data for last 6 months based on total revenue
    const chartData = [
      { label: 'Jan', value: totalRevenue * 0.4 },
      { label: 'Feb', value: totalRevenue * 0.6 },
      { label: 'Mar', value: totalRevenue * 0.8 },
      { label: 'Apr', value: totalRevenue * 0.5 },
      { label: 'May', value: totalRevenue * 0.9 },
      { label: 'Jun', value: totalRevenue },
    ];

    return { totalRevenue, totalCost, margin, topCategories, chartData };
  }, [org.id]);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.15em] text-[#0d0d0d] border-b border-[var(--theme-tertiary)] pb-3 mt-4">
        <BarChart3 size={16} className="text-[var(--theme-primary)]" />
        Reports & Analytics
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <WidgetPanel title="Revenue Analytics">
           <div className="flex-1 flex flex-col min-h-[220px]">
             {metrics.totalRevenue === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center gap-2 text-slate-400">
                  <LineChart size={32} className="opacity-30 mb-2" />
                  <span className="text-[10px] font-bold uppercase tracking-[0.15em]">No data yet</span>
                </div>
             ) : (
                <RoundedBarChart data={metrics.chartData} color="#6366f1" />
             )}
           </div>
        </WidgetPanel>

        <div className="flex flex-col gap-4">
          <WidgetPanel title="Sales vs Purchase Margin">
             <div className="flex flex-col gap-3 py-2">
                <div className="flex justify-between items-center">
                  <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Total Revenue</span>
                  <span className="text-sm font-bold text-emerald-600">₹{metrics.totalRevenue.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Total Purchase Cost</span>
                  <span className="text-sm font-bold text-rose-600">₹{metrics.totalCost.toFixed(2)}</span>
                </div>
                <div className="w-full h-px bg-[var(--theme-tertiary)] my-1"></div>
                <div className="flex justify-between items-center">
                  <span className="text-xs font-bold uppercase tracking-[0.14em] text-[#0d0d0d]">Gross Margin</span>
                  <span className={`text-lg font-bold ${metrics.margin >= 0 ? 'text-[var(--theme-primary)]' : 'text-rose-600'}`}>
                    ₹{metrics.margin.toFixed(2)}
                  </span>
                </div>
             </div>
          </WidgetPanel>

          <WidgetPanel title="Category-wise Revenue">
            <ul className="divide-y divide-[var(--theme-tertiary)]">
              {metrics.topCategories.length === 0 && <li className="py-2 text-center text-xs font-semibold uppercase tracking-[0.14em] text-slate-300">No data</li>}
              {metrics.topCategories.map(([cat, amount], idx) => (
                <li key={cat} className="py-2 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <PieChart size={12} className="text-slate-400" />
                    <span className="font-bold text-slate-600 truncate pr-4">{cat}</span>
                  </div>
                  <span className="font-bold text-[#0d0d0d]">₹{amount.toFixed(2)}</span>
                </li>
              ))}
            </ul>
          </WidgetPanel>
        </div>
      </div>


    </div>
  );
};

ReportsModule.propTypes = {
  org: PropTypes.object.isRequired,
};
